import { router, usePage } from '@inertiajs/react'
import AccountLayout from '@/layouts/AccountLayout'
import type { PricingProps, SharedProps } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface ExpiredProps {
  pricing: PricingProps
  currency_locked: boolean
}

const CURRENCY_LABEL: Record<string, string> = {
  GBP: 'GBP (£)',
  USD: 'USD ($)',
  EUR: 'EUR (€)'
}

export default function Expired({ pricing, currency_locked }: ExpiredProps) {
  const { current_membership } = usePage<{ props: SharedProps }>().props as unknown as SharedProps
  const ap = useAccountPath()
  // Only owners can start checkout (server enforces require_owner!). A signed-in
  // non-owner sees a "contact your owner" note instead of buttons that bounce.
  const isMember = !!current_membership
  const isOwner = current_membership?.owner ?? false
  const canSubscribe = isOwner || !isMember

  const price = pricing.prices[pricing.currency]

  function subscribe(interval: 'monthly' | 'annual') {
    router.post(ap('/billing/checkout'), { interval })
  }

  function changeCurrency(currency: string) {
    router.patch(ap('/billing/currency'), { currency }, { preserveScroll: true })
  }

  return (
    <AccountLayout>
      <div className="max-w-2xl mx-auto py-8 space-y-6">
        <div className="card bg-base-100">
          <div className="card-body text-center">
            <span className="iconify lucide--lock size-12 mx-auto text-warning" />
            <h1 className="text-3xl font-bold mt-2">Your plan is inactive</h1>
            <p className="text-base-content/70 mt-2">
              Your surveys and data are preserved indefinitely.
              Subscribe to keep creating new surveys, exporting reports, and using the API.
            </p>
          </div>
        </div>

        {canSubscribe ? (
          <div className="card bg-base-100 border-2 border-primary">
            <div className="card-body">
              <h2 className="card-title text-xl">Subscribe to Pro</h2>

              {isOwner && !currency_locked && (
                <fieldset className="fieldset">
                  <label className="label" htmlFor="billing-currency">Currency</label>
                  <select
                    id="billing-currency"
                    className="select select-sm w-full sm:w-56"
                    value={pricing.currency}
                    onChange={(e) => changeCurrency(e.target.value)}
                  >
                    {(['GBP', 'USD', 'EUR'] as const).map((code) => (
                      <option key={code} value={code}>{CURRENCY_LABEL[code]}</option>
                    ))}
                  </select>
                  <p className="label whitespace-normal">
                    Set from your location when you signed up. You can change it until you subscribe.
                  </p>
                </fieldset>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                <button
                  onClick={() => subscribe('monthly')}
                  className="card bg-base-200 hover:bg-base-300 transition cursor-pointer text-start"
                >
                  <div className="card-body">
                    <h3 className="font-semibold">Monthly</h3>
                    <div className="text-2xl font-bold mt-1">
                      {price.symbol}{price.monthly}
                      <span className="text-sm text-base-content/60 font-normal"> / month</span>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => subscribe('annual')}
                  className="card bg-primary/5 hover:bg-primary/10 transition cursor-pointer text-start border border-primary"
                >
                  <div className="card-body">
                    <h3 className="font-semibold">Annual <span className="badge badge-primary badge-sm ms-1">Save ~15%</span></h3>
                    <div className="text-2xl font-bold mt-1">
                      {price.symbol}{price.annual}
                      <span className="text-sm text-base-content/60 font-normal"> / year</span>
                    </div>
                  </div>
                </button>
              </div>

              <p className="text-xs text-base-content/40 mt-4">
                All prices excl. VAT/sales tax, added at checkout at your local rate
                whichever currency you pick. Billing in {pricing.currency}.
              </p>
            </div>
          </div>
        ) : (
          <div role="alert" className="alert alert-soft alert-info">
            <span className="iconify lucide--info size-5" />
            <span>Only organization owners can manage billing. Ask an owner to reactivate your plan.</span>
          </div>
        )}

        <p className="text-center text-sm text-base-content/60">
          Need SSO, SOC 2, or custom contracts? <a href="mailto:hello@envisialearning.com" className="link link-primary">Talk to us about Enterprise</a>.
        </p>
      </div>
    </AccountLayout>
  )
}
